import type { GrantPolicySpec } from '@/modules/catalog/domain/entities/catalog.entity';
import type { GrantedPerk } from './entities/user.entity';

/**
 * Ordem de consumo da mesma capacidade (§1.1 do plano de refactor de perks):
 *
 * 1. **Uso ilimitado** (`permanent`, `time_bounded_unlimited_usage`) — enquanto
 *    houver um vivo, usar a feature não decrementa crédito nenhum.
 * 2. **Crédito que evapora antes** — menor prazo primeiro (`expiresAt`, ou o
 *    `renewAt` de um recorrente: a cota que reseta antes é a que se perde antes).
 * 3. **Crédito sem prazo** — o que nunca evapora é o último a ser gasto.
 *
 * Desempate final: menor `remainingUsages`. É use-it-or-lose-it — a ordem que
 * nunca destrói valor do usuário.
 *
 * ⚠️ Isto é **previsão**, não leitura de estado: não existe caminho de consumo
 * no back ainda (§1.1 — `consumeUsage()` não tem chamador). A tela mostra a
 * ordem que valerá quando o resolvedor existir.
 */

/**
 * Kind que dá uso ilimitado — derivado da spec publicada, não de uma lista
 * local: quem não tem contador de usos não gasta crédito.
 */
function isUnlimitedUsageKind(spec: GrantPolicySpec | undefined) {
  return spec?.params.maxUsages === 'forbidden';
}

/** O prazo pelo qual o pacote evapora: o que vier primeiro entre os dois. */
function deadlineOf(pkg: GrantedPerk): number | null {
  const { expiresAt, renewAt } = pkg.grantPolicyParamsSnapshot ?? {};
  const times = [expiresAt, renewAt]
    .map((iso) => (iso ? new Date(iso).getTime() : Number.NaN))
    .filter((t) => !Number.isNaN(t));
  return times.length > 0 ? Math.min(...times) : null;
}

const UNLIMITED = 0;
const CREDIT_WITH_DEADLINE = 1;
const CREDIT_WITHOUT_DEADLINE = 2;

function tierOf(
  pkg: GrantedPerk,
  specs: GrantPolicySpec[] | undefined,
): number {
  const spec = specs?.find((s) => s.kind === pkg.grantPolicyKind);
  if (isUnlimitedUsageKind(spec)) return UNLIMITED;
  return deadlineOf(pkg) === null
    ? CREDIT_WITHOUT_DEADLINE
    : CREDIT_WITH_DEADLINE;
}

/**
 * Os pacotes na ordem em que seriam consumidos. Só faz sentido sobre os pacotes
 * **ativos** de uma mesma capacidade — cabe ao chamador filtrar.
 */
export function orderByConsumptionPriority(
  packages: GrantedPerk[],
  specs: GrantPolicySpec[] | undefined,
): GrantedPerk[] {
  return [...packages].sort((a, b) => {
    const tierDiff = tierOf(a, specs) - tierOf(b, specs);
    if (tierDiff !== 0) return tierDiff;

    const deadlineA = deadlineOf(a);
    const deadlineB = deadlineOf(b);
    if (deadlineA !== null && deadlineB !== null && deadlineA !== deadlineB) {
      return deadlineA - deadlineB;
    }

    const remainingA =
      a.grantPolicyParamsSnapshot?.remainingUsages ?? Number.POSITIVE_INFINITY;
    const remainingB =
      b.grantPolicyParamsSnapshot?.remainingUsages ?? Number.POSITIVE_INFINITY;
    return remainingA - remainingB;
  });
}
